'use client';

import { useState, useMemo } from 'react';

interface RankingRow {
  rank: number;
  code: string;
  name: string;
  flag: string;
  visaFree: number;
  voa: number;
  eVisa: number;
  visaRequired: number;
  accessScore: number;
  slug: string;
}

type SortKey = 'rank' | 'name' | 'visaFree' | 'voa' | 'accessScore';

interface PassportRankingTableProps {
  rows: RankingRow[];
}

export function PassportRankingTable({ rows }: PassportRankingTableProps) {
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [sortAsc, setSortAsc] = useState(true);

  const filtered = useMemo(() => {
    const lower = filter.toLowerCase();
    let result = lower ? rows.filter(r => r.name.toLowerCase().includes(lower)) : rows;

    result = [...result].sort((a, b) => {
      if (sortKey === 'name') {
        return sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      }
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      return sortAsc ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    return result;
  }, [rows, filter, sortKey, sortAsc]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      // Default: rank ascending, numbers descending, name ascending
      setSortAsc(key === 'rank' || key === 'name');
    }
  }

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return '';
    return sortAsc ? ' \u25B2' : ' \u25BC';
  };

  const maxScore = useMemo(() => Math.max(...rows.map(r => r.accessScore), 1), [rows]);

  return (
    <div className="mt-12 group/table">
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-text-primary">
            Full Ranking — All {rows.length} Countries
          </h2>
          <p className="text-xs font-medium text-text-dim uppercase tracking-wider mt-1">
            Sort by mobility score or country name
          </p>
        </div>
        <input
          type="search"
          placeholder="Filter by country name…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-4 py-2 rounded-xl border border-border-subtle bg-surface-elevated text-sm w-full max-w-xs focus:ring-2 focus:ring-brand/20 outline-none transition-all"
          aria-label="Filter countries"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border-subtle bg-surface-elevated shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-surface-sunken/50 border-b border-border-subtle">
              <th className="px-6 py-4 cursor-pointer select-none text-right text-[10px] font-black uppercase tracking-widest text-text-dim hover:text-brand transition-colors" onClick={() => handleSort('rank')}>
                Rank{sortIcon('rank')}
              </th>
              <th className="px-6 py-4 cursor-pointer select-none text-[10px] font-black uppercase tracking-widest text-text-dim hover:text-brand transition-colors" onClick={() => handleSort('name')}>
                Country{sortIcon('name')}
              </th>
              <th className="px-6 py-4 cursor-pointer select-none text-right text-[10px] font-black uppercase tracking-widest text-text-dim hover:text-brand transition-colors" onClick={() => handleSort('visaFree')}>
                Visa-Free{sortIcon('visaFree')}
              </th>
              <th className="px-6 py-4 cursor-pointer select-none text-right text-[10px] font-black uppercase tracking-widest text-text-dim hover:text-brand transition-colors" onClick={() => handleSort('voa')}>
                VOA{sortIcon('voa')}
              </th>
              <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-widest text-text-dim">e-Visa</th>
              <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-widest text-text-dim">Required</th>
              <th className="px-6 py-4 cursor-pointer select-none text-[10px] font-black uppercase tracking-widest text-text-dim hover:text-brand transition-colors w-[200px]" onClick={() => handleSort('accessScore')}>
                Mobility Score{sortIcon('accessScore')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle/50">
            {filtered.map(row => {
              const percentage = (row.accessScore / maxScore) * 100;
              
              return (
                <tr key={row.code} className="hover:bg-brand/[0.02] transition-colors group/row">
                  <td className="px-6 py-4 text-right font-black text-text-dim tabular-nums">#{row.rank}</td>
                  <td className="px-6 py-4">
                    <a href={`/${row.slug}`} className="inline-flex items-center gap-2 hover:text-brand transition-colors font-bold text-sm">
                      <span className="text-lg">{row.flag}</span>
                      <span className="line-clamp-1">{row.name}</span>
                    </a>
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-sm text-text-secondary">{row.visaFree}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-sm text-text-secondary">{row.voa}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-sm text-text-secondary">{row.eVisa}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-sm text-text-secondary">{row.visaRequired}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-black text-brand tabular-nums">{row.accessScore}</span>
                        <span className="text-[9px] font-bold text-text-dim uppercase tracking-tighter opacity-0 group-hover/row:opacity-100 transition-opacity">
                            {Math.round(percentage)}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-surface-sunken rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-brand to-copper rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>


      {filtered.length === 0 && filter && (
        <p className="mt-4 text-sm text-text-secondary">No countries match &ldquo;{filter}&rdquo;</p>
      )}
    </div>
  );
}
